import type { ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

// Primitivos da coluna de resultado do sandbox (rótulo de seção, delta colorido,
// linha de métrica), fiéis ao protótipo V3ResultsColumn: layout label | valor | Δ, o delta
// sempre com seta + cor (nunca só cor — Princípio de acessibilidade).

/** Classe do rótulo de seção (caps pequenas). Compartilhada com cabeçalhos que têm ação à direita. */
export const SECTION_LABEL_CLS =
  "text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground";

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className={cn(SECTION_LABEL_CLS, "mb-2.5")}>{children}</p>;
}

interface DeltaProps {
  value: number | null;
  unit?: string;
  /** true ⇒ menor é melhor (ex.: tempo) — inverte a cor sem mudar o sinal. */
  invert?: boolean;
}

// Δ colorido: verde melhora / vermelho piora / neutro em zero ou ausente. Seta + sinal reforçam
// a direção para leitura sem cor.
export function DeltaValue({ value, unit = "pp", invert = false }: DeltaProps) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const good = invert ? value < 0 : value > 0;
  const tone =
    value === 0 ? "text-muted-foreground" : good ? "text-emerald-600" : "text-destructive";
  const abs = Math.abs(value) >= 100 ? value.toLocaleString("pt-BR") : value;
  return (
    <span
      className={cn("inline-flex items-center gap-0.5 font-mono text-[11px] tabular-nums", tone)}
    >
      {value > 0 ? (
        <ArrowUp className="h-2.5 w-2.5" />
      ) : value < 0 ? (
        <ArrowDown className="h-2.5 w-2.5" />
      ) : null}
      {value > 0 ? "+" : ""}
      {abs} {unit}
    </span>
  );
}

interface MetricRowProps {
  label: string;
  value: string;
  delta?: number | null;
  unit?: string;
  /** Sem base p/ comparar (1ª rodada da linha nova) ⇒ mostra "base" no lugar do delta. */
  isBase?: boolean;
}

// Linha de métrica: rótulo à esquerda, valor mono no meio, Δ (ou "base") à direita.
export function MetricRow({ label, value, delta, unit = "pp", isBase }: MetricRowProps) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-2 border-b border-border/60 py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono text-[12.5px] font-medium tabular-nums">{value}</span>
      <span className="min-w-[54px] text-right">
        {isBase ? (
          <span className="text-[10.5px] text-muted-foreground">base</span>
        ) : (
          <DeltaValue value={delta ?? null} unit={unit} />
        )}
      </span>
    </div>
  );
}
